<template>
    <transition name="bmv-modal-fade">
        <div
            v-if="value"
            class="bmv-modal-overlay"
            role="dialog"
            aria-modal="true"
            data-testid="bmv-modal"
            @mousedown.self="onOverlayClick"
        >
            <div
                class="bmv-modal"
                :class="[`bmv-modal--${size}`]"
                @mousedown.stop
            >
                <header v-if="$slots.header || title" class="bmv-modal-header">
                    <slot name="header">
                        <h2 class="bmv-modal-title">{{ title }}</h2>
                    </slot>
                    <button
                        v-if="closable"
                        type="button"
                        class="bmv-modal-close"
                        :aria-label="closeLabel"
                        @click="close('close-btn')"
                    >
                        <i class="fal fa-times"></i>
                    </button>
                </header>

                <div class="bmv-modal-body">
                    <slot />
                </div>

                <footer v-if="$slots.footer" class="bmv-modal-footer">
                    <slot name="footer" />
                </footer>
            </div>
        </div>
    </transition>
</template>

<script>
const SIZES = ['sm', 'md', 'lg', 'xl']

export default {
    name: 'BmvModal',
    props: {
        value: { type: Boolean, default: false },
        title: { type: String, default: '' },
        size: {
            type: String,
            default: 'md',
            validator: (v) => SIZES.includes(v)
        },
        closable: { type: Boolean, default: true },
        closeOnOverlay: { type: Boolean, default: true },
        closeOnEsc: { type: Boolean, default: true },
        closeLabel: { type: String, default: 'Close' }
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (typeof window === 'undefined') return
                if (open) {
                    document.addEventListener('keydown', this.onKeydown)
                    document.body.classList.add('bmv-modal-open')
                } else {
                    document.removeEventListener('keydown', this.onKeydown)
                    document.body.classList.remove('bmv-modal-open')
                }
            }
        }
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeydown)
        document.body.classList.remove('bmv-modal-open')
    },
    methods: {
        close(source) {
            this.$emit('close', source)
            this.$emit('input', false)
        },
        onOverlayClick() {
            if (this.closeOnOverlay) this.close('overlay')
        },
        onKeydown(e) {
            if (e.key === 'Escape' && this.closeOnEsc && this.closable) {
                this.close('esc')
            }
        }
    }
}
</script>

<style>
body.bmv-modal-open {
    overflow: hidden;
}
</style>

<style scoped>
.bmv-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-4);
    z-index: 9999;
    backdrop-filter: blur(2px);
}

.bmv-modal {
    background: var(--v2-surface-0);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    width: 100%;
    max-height: calc(100vh - 2 * var(--v2-space-4));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: var(--v2-font-sans);
}

.bmv-modal--sm { max-width: 360px; }
.bmv-modal--md { max-width: 520px; }
.bmv-modal--lg { max-width: 760px; }
.bmv-modal--xl { max-width: 960px; }

.bmv-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-4);
    padding: var(--v2-space-5) var(--v2-space-6);
    border-bottom: 1px solid var(--v2-border-subtle);
}

.bmv-modal-title {
    margin: 0;
    font-size: var(--v2-text-lg-size);
    line-height: var(--v2-text-lg-line);
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-semibold);
}

.bmv-modal-close {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: var(--v2-radius-sm);
    background: transparent;
    color: var(--v2-text-3);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.bmv-modal-close:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.bmv-modal-body {
    padding: var(--v2-space-5) var(--v2-space-6);
    overflow-y: auto;
    color: var(--v2-text-2);
    flex: 1;
}

.bmv-modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--v2-space-2);
    padding: var(--v2-space-4) var(--v2-space-6);
    border-top: 1px solid var(--v2-border-subtle);
    background: var(--v2-surface-1);
}

/* Transition */
.bmv-modal-fade-enter-active,
.bmv-modal-fade-leave-active {
    transition: opacity var(--v2-transition-base);
}
.bmv-modal-fade-enter-active .bmv-modal,
.bmv-modal-fade-leave-active .bmv-modal {
    transition: transform var(--v2-transition-base),
        opacity var(--v2-transition-base);
}

.bmv-modal-fade-enter,
.bmv-modal-fade-leave-to {
    opacity: 0;
}
.bmv-modal-fade-enter .bmv-modal,
.bmv-modal-fade-leave-to .bmv-modal {
    transform: translateY(-8px);
    opacity: 0;
}
</style>
